import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { useSubsidyCalculator } from '../hooks/useSubsidyCalculator';
import { SubsidyTable } from './subsidyTable';

interface Props {
  accountId: string;
}

export const SubsidyCalculatorPanel = ({ accountId }: Props) => {
  const {
    selectedProgramId,
    programOptions,
    isOptionsLoading,
    isProgramLoading,
    program,
    isSubsidyEligible,
    subsidyPercent,
    dnetPercent,
    handleProgramChange,
  } = useSubsidyCalculator({ accountId });

  return (
    <CardContainer title="Subsidy Calculator">
      <Stack gap="1.5rem">
        <FormControl size="small" fullWidth variant="standard" disabled={isOptionsLoading}>
          <InputLabel>Finance Program</InputLabel>
          <Select
            label="Finance Program"
            value={selectedProgramId}
            onChange={e => handleProgramChange(e.target.value)}
            endAdornment={isOptionsLoading ? <CircularProgress size={16} sx={{ mr: 3 }} /> : null}
          >
            {programOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isProgramLoading && (
          <Stack alignItems="center" justifyContent="center" py="2rem">
            <CircularProgress />
          </Stack>
        )}

        {!isProgramLoading && program && !isSubsidyEligible && (
          <Alert severity="error">This program is not eligible for subsidy</Alert>
        )}

        {!isProgramLoading && program && isSubsidyEligible && (
          <Stack gap="1rem">
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <Stack gap="0.25rem">
                <Typography variant="caption" color="text.secondary">
                  Subsidy %
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {subsidyPercent}%
                </Typography>
              </Stack>

              <Stack gap="0.25rem">
                <Typography variant="caption" color="text.secondary">
                  DNET %
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {dnetPercent}%
                </Typography>
              </Stack>
            </Box>

            <SubsidyTable subsidyPercent={subsidyPercent} dnetCoefficient={dnetPercent / 100} />
          </Stack>
        )}
      </Stack>
    </CardContainer>
  );
};
